export function formatDuration(seconds?: number): string {
    if (!seconds) return '--'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}h ${minutes}m`
}

export function formatDurationHHMM(seconds?: number): string {
    if (!seconds) return '--:--'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

export function formatTimer(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

export function formatDateShort(dateString: string): string {
    return parseDate(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function formatDateWithWeekday(dateString: string): string {
    return parseDate(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    })
}

export function formatDateLocale(dateString: string): string {
    return parseDate(dateString).toLocaleDateString()
}

export function formatTimeOfDay(dateString: string): string {
    return parseDate(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    })
}

export function formatTimeOfDayLocale(dateString: string): string {
    return parseDate(dateString).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
    })
}

export function calculateElapsed(startTime: string | Date): number {
    const start = typeof startTime === 'string' ? parseDate(startTime) : startTime
    const now = Date.now()
    return Math.floor((now - start.getTime()) / 1000)
}

export function secondsToHours(seconds: number): number {
    return seconds / 3600
}

export function minutesToSeconds(minutes: number): number {
    return minutes * 60
}

export function secondsToMinutes(seconds: number): number {
    return Math.floor(seconds / 60)
}

export function formatHoursReadable(hours: number): string {
    const abs = Math.abs(hours)
    const totalMinutes = Math.round(abs * 60)
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    const formatted = `${h}:${m.toString().padStart(2, '0')}`
    return hours < 0 ? `-${formatted}` : formatted
}

export function formatHours(hours: number, precision = 2, suffix = 'h'): string {
    return `${hours.toFixed(precision)}${suffix}`
}

export function getNetWorkSeconds(entry: {
    duration?: number
    break_time?: number
    entry_type?: string
}): number {
    if (entry.entry_type === 'break') return -(entry.duration || 0)
    return entry.duration || 0
}

interface TimeInterval {
    clock_in: string
    clock_out?: string
    duration?: number
    entry_type?: string
    status?: string
}

export interface Interval {
    start: number
    end: number
}

// Resolves an entry's [start, end] in epoch ms, falling back to clock_in +
// duration for completed entries and "now" for active ones.
function entryInterval(e: TimeInterval): Interval {
    const start = parseDate(e.clock_in).getTime()
    const end = e.clock_out
        ? parseDate(e.clock_out).getTime()
        : e.status === 'active'
          ? Date.now()
          : start + (e.duration || 0) * 1000
    return { start, end }
}

export function toWorkIntervals(workEntries: TimeInterval[]): Interval[] {
    return workEntries
        .filter(e => e.clock_in)
        .map(entryInterval)
        .filter(i => i.end > i.start)
}

interface IdentifiedEntry extends TimeInterval {
    id: string
    user?: string
}

/**
 * Builds a per-entry lookup of the work intervals a break should net against.
 *
 * Break overlap must be scoped to the SAME user's SAME day — the same grouping
 * the approval screen uses (sumNetWorkSeconds per user/day). A single global
 * interval set would let one user's break subtract against another user's (or
 * another day's) concurrent work, so flat exports would not reconcile with the
 * approval-screen totals. Keyed by entry id; every entry maps to its group's
 * work intervals.
 */
export function workIntervalsByEntry<E extends IdentifiedEntry>(
    entries: E[]
): Map<string, Interval[]> {
    const groups = new Map<string, E[]>()
    for (const entry of entries) {
        if (entry.status === 'rejected') continue
        const dateKey = entry.clock_in ? getISODate(entry.clock_in) : ''
        const groupKey = `${entry.user ?? ''}|${dateKey}`
        const existing = groups.get(groupKey)
        if (existing) existing.push(entry)
        else groups.set(groupKey, [entry])
    }

    const byEntry = new Map<string, Interval[]>()
    for (const groupEntries of groups.values()) {
        const intervals = toWorkIntervals(groupEntries.filter(e => e.entry_type !== 'break'))
        for (const entry of groupEntries) {
            byEntry.set(entry.id, intervals)
        }
    }
    return byEntry
}

/**
 * Seconds of a single break that fall within any of the given work intervals.
 * This is the portion that is actually netted out of paid time — a break with
 * no overlapping work (e.g. taken between jobs) contributes 0.
 */
export function breakOverlapSeconds(brk: TimeInterval, workIntervals: Interval[]): number {
    if (!brk.clock_in) return 0
    const { start: brkStart, end: brkEnd } = entryInterval(brk)

    let overlap = 0
    for (const work of workIntervals) {
        const overlapStart = Math.max(brkStart, work.start)
        const overlapEnd = Math.min(brkEnd, work.end)
        if (overlapEnd > overlapStart) {
            overlap += (overlapEnd - overlapStart) / 1000
        }
    }
    return overlap
}

/**
 * Sums work seconds with overlap-aware break subtraction.
 * Breaks only subtract time that overlaps with active work entries,
 * not their full duration.
 */
export function sumNetWorkSeconds(entries: TimeInterval[]): number {
    const workEntries = entries.filter(e => e.entry_type !== 'break' && e.status !== 'rejected')
    const breakEntries = entries.filter(e => e.entry_type === 'break' && e.status !== 'rejected')

    const workTotal = workEntries.reduce((acc, e) => {
        if (e.status === 'active') return acc + calculateElapsed(e.clock_in)
        return acc + (e.duration || 0)
    }, 0)

    if (breakEntries.length === 0) return workTotal

    const workIntervals = toWorkIntervals(workEntries)
    const breakOverlap = breakEntries.reduce(
        (acc, brk) => acc + breakOverlapSeconds(brk, workIntervals),
        0
    )

    return Math.max(0, workTotal - breakOverlap)
}

export function getWeekBounds(date: Date = new Date()): {
    start: string
    end: string
} {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)

    const monday = new Date(d.setDate(diff))
    monday.setHours(0, 0, 0, 0)

    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)
    sunday.setHours(23, 59, 59, 999)

    return {
        start: pbDate(monday),
        end: pbDate(sunday),
    }
}

export function getCurrentWeekBounds(): { start: string; end: string } {
    return getWeekBounds()
}

export function getCurrentISOString(): string {
    return new Date().toISOString()
}

export function getTodayISODate(): string {
    const d = new Date()
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export function getISODate(isoString: string): string {
    const d = parseDate(isoString)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export function parseDate(dateString: string | Date): Date {
    if (dateString instanceof Date) return dateString
    if (!dateString) return new Date()
    const normalized = dateString.replace(' ', 'T')
    // Date-only strings (e.g. "2026-03-30") are parsed as UTC by spec.
    // Append T00:00:00 to force local-time interpretation.
    if (!normalized.includes('T')) return new Date(`${normalized}T00:00:00`)
    return new Date(normalized)
}

export function pbDate(date: Date = new Date()) {
    return date.toISOString().replace('T', ' ')
}

type MinimalEntry = {
    clock_in?: string
    clock_out?: string
}
export function calculateDuration(entry: MinimalEntry) {
    const outT = entry.clock_out ? parseDate(entry.clock_out).getTime() : NaN
    const inT = entry.clock_in ? parseDate(entry.clock_in).getTime() : NaN

    if (Number.isNaN(outT) || Number.isNaN(inT)) {
        return 0
    }

    return (outT - inT) / 1000
}

interface DateRange {
    startDate: Date
    endDate: Date
}

export function getDayRange(date: Date): DateRange {
    const startDate = new Date(date)
    startDate.setHours(0, 0, 0, 0)

    const endDate = new Date(date)
    endDate.setHours(23, 59, 59, 999)

    return { startDate, endDate }
}

const FIVE_MINUTES_MS = 5 * 60 * 1000

export function roundDownToFiveMinutes(time: number): number {
    return Math.floor(time / FIVE_MINUTES_MS) * FIVE_MINUTES_MS
}

export function roundUpToFiveMinutes(time: number): number {
    return Math.ceil(time / FIVE_MINUTES_MS) * FIVE_MINUTES_MS
}

export function formatTimeOnly(date: Date): string {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export function formatDateParam(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export function parseDateParam(param: string | undefined): Date | null {
    if (!param) return null
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(param)
    if (!match) return null
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    if (Number.isNaN(date.getTime())) return null
    return date
}
